import { Link, MediaQuery, Text, useMediaQuery } from "@certn/cascadia-react";
import { formatDateTime, slugify } from "@certn/utils";
import { css } from "@emotion/css";
import facepaint from "facepaint";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface CaseDetailsProps {
  caseID: string | null;
  dateOrdered: string | null;
  dateGenerated?: string | null;
  reportCompleted: string | null;
  isPDF: boolean;
  isLoading?: boolean;
}

function showAllFields(
  fields: { title: string; value: string | null }[],
  mediaQuery: facepaint.DynamicStyleFunction,
  isPDF: boolean,
  isMobile?: boolean,
  isLoading?: boolean
) {
  return (
    <div
      className={css(
        styles.gridContainer,
        mediaQuery({
          display: ["block", "block", "grid"],
          marginBottom: ["var(--space-3)", "var(--space-3)", 0],
        })
      )}
    >
      {fields.map((field: { title: string; value: string | null }, i) => {
        if (!isPDF && field.title === "Date generated") {
          return;
        }
        return (
          <div
            key={i}
            className={css(
              styles.caseDetails,
              mediaQuery({
                marginBottom: ["var(--space-3)", "var(--space-3)", 0],
              })
            )}
          >
            <Text style="summary/content-secondary">{field.title}</Text>
            <Text
              style="summary/content-primary"
              testID={
                isMobile
                  ? "sm-case-details-" + slugify(field.title) + "-value"
                  : "lg-case-details-" + slugify(field.title) + "-value"
              }
              isLoading={isLoading}
            >
              {field.value || "--"}
            </Text>
          </div>
        );
      })}
    </div>
  );
}
export function CaseDetails({
  caseID,
  dateOrdered,
  dateGenerated,
  reportCompleted,
  isPDF,
  isLoading,
}: CaseDetailsProps) {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const { mediaQuery } = useMediaQuery();
  const fields = [
    { title: t("Case ID"), value: caseID },
    {
      title: t("Date ordered"),
      value: formatDateTime(dateOrdered, "MMMM dd, yyyy h:mm a", "--"),
    },
    {
      title: t("Date generated"),
      value: formatDateTime(dateGenerated, "MMMM dd, yyyy h:mm a", "--"),
    },
    {
      title: t("Report completed"),
      value: formatDateTime(reportCompleted, "MMMM dd, yyyy h:mm a", "--"),
    },
  ];

  return (
    <div style={{ marginTop: "var(--space-4)" }}>
      <div className={styles.header}>
        <Text style="global/heading-medium">{t("Case details")}</Text>
      </div>
      <MediaQuery sm md type="show">
        {!showMore ? (
          <>
            <div
              className={css(
                styles.caseDetails,
                mediaQuery({
                  marginBottom: ["var(--space-3)", "var(--space-3)", 0],
                })
              )}
            >
              <Text style="summary/content-secondary">{t("Case ID")}</Text>
              <Text
                style="summary/content-primary"
                testID="sm-case-details-case-id-value"
                isLoading={isLoading}
              >
                {caseID || "--"}
              </Text>
            </div>
            <div
              className={css(
                styles.caseDetails,
                mediaQuery({
                  marginBottom: ["var(--space-3)", "var(--space-3)", 0],
                })
              )}
            >
              <Text style="summary/content-secondary">{t("Date ordered")}</Text>
              <Text
                style="summary/content-primary"
                testID="sm-case-details-date-ordered-value"
                isLoading={isLoading}
              >
                {formatDateTime(dateOrdered, "MMMM dd, yyyy h:mm a", "--")}
              </Text>
            </div>
          </>
        ) : (
          showAllFields(fields, mediaQuery, isPDF, true, isLoading)
        )}
      </MediaQuery>
      <MediaQuery sm md type="hide">
        {showAllFields(fields, mediaQuery, isPDF, undefined, isLoading)}
      </MediaQuery>
      <MediaQuery sm md type="show">
        {!showMore ? (
          <Link
            testID="sm-case-details-show-more"
            onPress={() => {
              setShowMore(!showMore);
            }}
            textColor="text-weak"
          >
            {t("Show more")}
          </Link>
        ) : (
          <Link
            testID="sm-case-details-show-less"
            onPress={() => {
              setShowMore(!showMore);
            }}
            textColor="text-weak"
          >
            {t("Show less")}
          </Link>
        )}
      </MediaQuery>
    </div>
  );
}

const styles = {
  gridContainer: css({
    gridTemplateColumns: "1fr 1fr 1fr",
    justifyContent: "space-between",
    alignItems: "start",
    columnGap: "var(--space-3)",
    rowGap: "var(--space-3)",
  }),
  caseDetails: css({
    display: "grid",
  }),
  header: css({
    marginBottom: "var(--space-3)",
  }),
  gridItem: css({
    wordWrap: "break-word",
    wordBreak: "break-word",
  }),
};
